import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, message, Divider, Typography, Switch } from 'antd';
import { adminTasksApi } from '../../../services/api/adminTasks';
import { AdminTaskType, AdminTaskStatus } from '../../../types';

const { TextArea } = Input;
const { Text } = Typography;

interface AutoPipelineConfigModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  storyId: string;
  storyName: string;
}

const AutoPipelineConfigModal: React.FC<AutoPipelineConfigModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  storyId,
  storyName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const detail = {
        input: {
          sourceStoryId: storyId,
          storyName: storyName,
          chapterName: values.chapterName,
          instruction: values.instruction,
          outlinePassScore: values.outlinePassScore,
          maxOutlineRetries: values.maxOutlineRetries,
          contentPassScore: values.contentPassScore,
          maxContentRetries: values.maxContentRetries,
          panelMediaType: values.panelMediaType,
          generateHook: values.generateHook,
          hookPassScore: values.generateHook ? values.hookPassScore : undefined,
          maxHookRetries: values.generateHook ? values.maxHookRetries : undefined,
        },
        progress: 0,
        targetId: storyId,
      };

      await adminTasksApi.createTask({
        type: AdminTaskType.FULL_AUTO_CHAPTER_TO_VIDEO,
        detail: detail as any,
      });

      message.success('Đã khởi tạo tiến trình tự động thành công!');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation error, no need to log as system error
        return;
      }
      console.error('Failed to create auto task:', error);
      message.error('Không thể khởi tạo tiến trình: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span>🚀 Khởi tạo tiến trình Full Auto - <b>{storyName}</b></span>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={650}
      okText="Bắt đầu ngay"
      cancelText="Hủy bỏ"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          outlinePassScore: 85,
          maxOutlineRetries: 5,
          contentPassScore: 85,
          maxContentRetries: 5,
          panelMediaType: 'IMAGE',
          generateHook: true,
          hookPassScore: 85,
          maxHookRetries: 5,
        }}
      >
        <Divider orientation={"left" as any}>Thông tin Chapter</Divider>
        <Form.Item
          name="chapterName"
          label="Tên Chapter"
          rules={[{ required: true, message: 'Vui lòng nhập tên chapter' }]}
        >
          <Input placeholder="Ví dụ: Chương 1: Sự khởi đầu" />
        </Form.Item>

        <Form.Item
          name="instruction"
          label="Chỉ dẫn nội dung (Prompt định hướng)"
        >
          <TextArea 
            rows={4} 
            placeholder="Mô tả sơ qua về nội dung hoặc các tình tiết quan trọng AI cần tuân thủ..." 
          />
        </Form.Item>

        <Divider orientation={"left" as any}>Cấu hình Đánh giá & Retry</Divider>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="outlinePassScore"
            label="Ngưỡng điểm Outline"
            tooltip="Điểm AI chấm để chấp nhận bản Outline"
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="maxOutlineRetries"
            label="Số lần thử Outline max"
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="contentPassScore"
            label="Ngưỡng điểm Content"
            tooltip="Điểm AI chấm để chấp nhận bản Content"
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="maxContentRetries"
            label="Số lần thử Content max"
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Divider orientation={"left" as any}>Đạo diễn hình ảnh</Divider>
        <Form.Item
          name="panelMediaType"
          label="Thành phẩm Panel"
        >
          <Select options={[
            { label: 'Ảnh tĩnh (Mặc định)', value: 'IMAGE' },
            { label: 'Video chuyển động', value: 'VIDEO' },
            { label: 'Mix (AI tự quyết định)', value: 'MIX' },
          ]} />
        </Form.Item>

        <Divider orientation={"left" as any}>Hook mở đầu</Divider>
        <Form.Item
          name="generateHook"
          label="Tự động tạo Hook mở đầu"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.generateHook !== currentValues.generateHook}>
          {({ getFieldValue }) => 
            getFieldValue('generateHook') && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="hookPassScore"
                  label="Ngưỡng điểm Hook"
                  tooltip="Điểm AI chấm để chấp nhận bản Hook"
                >
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  name="maxHookRetries"
                  label="Số lần thử Hook max"
                >
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            )
          }
        </Form.Item>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          * Hệ thống sẽ tự động sử dụng Voice, Image Style và các cấu hình mặc định của truyện này.
        </Text>
      </Form>
    </Modal>
  );
};

export default AutoPipelineConfigModal;
